import React from 'react';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  SplineSeries,
  LineSeries,
  Category,
  Legend,
  Tooltip,
} from '@syncfusion/ej2-react-charts';
import { useStateContext } from '../../contexts/ContextProvider';

const formatMonthLabel = (mStr) => {
  if (!mStr) return '';
  if (mStr.includes('-')) {
    const [year, m] = mStr.split('-');
    if (year && m) {
      const date = new Date(parseInt(year, 10), parseInt(m, 10) - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
  }
  return mStr;
};

const formatCompactCurrency = (val) => {
  if (typeof val !== 'number' || isNaN(val)) return '₦0';
  if (val === 0) return '₦0';
  if (Math.abs(val) >= 1_000_000_000) return `₦${(val / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (Math.abs(val) >= 1_000_000) return `₦${(val / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(val) >= 1_000) return `₦${(val / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return `₦${val.toLocaleString()}`;
};

const FinanceTrendChart = ({ trends = [] }) => {
  const { currentMode } = useStateContext();
  const isDark = currentMode === 'Dark';

  if (!Array.isArray(trends) || trends.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-gray-400">
        No trend data available for the selected period.
      </div>
    );
  }

  const formattedTrends = trends.map((item) => ({
    ...item,
    monthLabel: formatMonthLabel(item.month),
    income: Number(item.income) || 0,
    expenses: Number(item.expenses) || 0,
  }));

  const maxVal = Math.max(
    ...formattedTrends.map((t) => Math.max(t.income, t.expenses))
  );

  // If trends length is < 2, fall back to Line series to avoid Spline single-point rendering issues
  const seriesType = formattedTrends.length >= 2 ? 'Spline' : 'Line';

  const primaryXAxis = {
    valueType: 'Category',
    labelStyle: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      size: '12px',
      color: isDark ? '#9CA3AF' : '#64748B',
      fontWeight: '500',
    },
    lineStyle: { color: isDark ? '#374151' : '#E2E8F0', width: 1 },
    majorGridLines: { width: 0 },
    majorTickLines: { width: 0 },
  };

  const primaryYAxis = {
    minimum: 0,
    ...(maxVal === 0 && { maximum: 1000 }),
    labelStyle: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      size: '12px',
      color: isDark ? '#9CA3AF' : '#64748B',
      fontWeight: '500',
    },
    lineStyle: { width: 0 },
    majorTickLines: { width: 0 },
    minorTickLines: { width: 0 },
    majorGridLines: {
      color: isDark ? '#1F2937' : '#F1F5F9',
      width: 1,
      dashArray: '4,4',
    },
  };

  const legendSettings = {
    visible: true,
    position: 'Top',
    alignment: 'Far',
    textStyle: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      size: '12px',
      fontWeight: '600',
      color: isDark ? '#E5E7EB' : '#475569',
    },
  };

  const tooltip = {
    enable: true,
    shared: true,
    textStyle: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      size: '12px',
    },
  };

  const axisLabelRender = (args) => {
    if (args.axis.name === 'primaryYAxis') {
      args.text = formatCompactCurrency(args.value);
    }
  };

  return (
    <ChartComponent
      id="finance-trend-chart"
      height="340px"
      primaryXAxis={primaryXAxis}
      primaryYAxis={primaryYAxis}
      chartArea={{ border: { width: 0 } }}
      tooltip={tooltip}
      background={isDark ? '#33373E' : '#fff'}
      legendSettings={legendSettings}
      axisLabelRender={axisLabelRender}
    >
      <Inject services={[SplineSeries, LineSeries, Category, Legend, Tooltip]} />
      <SeriesCollectionDirective>
        <SeriesDirective
          dataSource={formattedTrends}
          xName="monthLabel"
          yName="income"
          name="Income"
          type={seriesType}
          splineType="Monotonic"
          width={3}
          marker={{
            visible: true,
            width: 7,
            height: 7,
            fill: '#10B981',
            border: { color: '#FFFFFF', width: 2 },
          }}
          fill="#10B981"
        />
        <SeriesDirective
          dataSource={formattedTrends}
          xName="monthLabel"
          yName="expenses"
          name="Expenses"
          type={seriesType}
          splineType="Monotonic"
          width={3}
          marker={{
            visible: true,
            width: 7,
            height: 7,
            fill: '#EF4444',
            border: { color: '#FFFFFF', width: 2 },
          }}
          fill="#EF4444"
        />
      </SeriesCollectionDirective>
    </ChartComponent>
  );
};

export default FinanceTrendChart;
